import { useEffect, useState } from 'react';
import { toast } from 'sonner';

/**
 * Hook to detect app updates and manage service worker lifecycle
 */
export const useAppUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [appVersion, setAppVersion] = useState(window.APP_VERSION || '3.0.0');

  useEffect(() => {
    // Listen for service worker update messages
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        console.log('[App] Update available:', event.data.version);
        setUpdateAvailable(true);
        setAppVersion(event.data.version);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, []);

  const refreshApp = () => {
    // Tell service worker to skip waiting
    if (window.newServiceWorker) {
      window.newServiceWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    // Force reload from server
    window.location.reload(true);
  };

  const clearCache = async () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
      toast.success('Cache cleared! Refreshing...');
      setTimeout(() => window.location.reload(true), 500);
    }
  };

  return {
    updateAvailable,
    appVersion,
    refreshApp,
    clearCache
  };
};

/**
 * Get app version info from service worker
 */
export const getVersionInfo = async () => {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    return { version: window.APP_VERSION || '3.0.0', buildTime: null, cacheName: null };
  }

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    
    messageChannel.port1.onmessage = (event) => {
      resolve(event.data);
    };

    navigator.serviceWorker.controller.postMessage(
      { type: 'GET_VERSION' },
      [messageChannel.port2]
    );

    // Timeout fallback
    setTimeout(() => {
      resolve({ version: window.APP_VERSION || '3.0.0', buildTime: null, cacheName: null });
    }, 1000);
  });
};

export default useAppUpdate;
